import React, { useState, useEffect } from 'react';
import { API } from '@/App';
import axios from 'axios';
import PublicLayout from '@/components/PublicLayout';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { GraduationCap, ExternalLink, BookOpen, Award, Users, Sparkles } from 'lucide-react';
import { trackClick } from '@/utils/tracking';

const TrainingProgramsPage = () => {
  const [cms, setCms] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/public`).then(res => setCms(res.data)).catch(() => {});
  }, []);

  const title = cms?.upskilling_title || 'Upskilling Courses from Wadhwani Foundation';
  const description = cms?.upskilling_description || 'Access free upskilling courses designed to enhance your teaching skills, digital literacy, and career readiness.';
  const link = cms?.upskilling_link || 'https://www.wadhwanifoundation.org/programs';

  const highlights = [
    { icon: BookOpen, label: 'Free Courses', desc: 'Industry-relevant programs at no cost' },
    { icon: Award, label: 'Certified', desc: 'Earn certificates on completion' },
    { icon: Users, label: 'Expert-Led', desc: 'Designed by education professionals' },
    { icon: Sparkles, label: 'Career Growth', desc: 'Skills that employers value' },
  ];

  return (
    <PublicLayout>
      <SEO
        title="Training & Upskilling"
        description="Free upskilling courses from Wadhwani Foundation for educators. Enhance teaching skills, digital literacy, and career readiness."
        path="/training"
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="training-programs-page">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
            <GraduationCap className="w-4 h-4" />
            Partner Program
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>
            {title}
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
            {description}
          </p>
        </div>

        {/* Highlights Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {highlights.map((h, i) => (
            <Card key={i} className="text-center border-slate-200">
              <CardContent className="pt-6 pb-5 px-4">
                <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center mx-auto mb-3">
                  <h.icon className="w-5 h-5 text-indigo-600" />
                </div>
                <p className="font-semibold text-slate-900 text-sm">{h.label}</p>
                <p className="text-xs text-slate-500 mt-1">{h.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA Card */}
        <Card className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-slate-800 border-0 overflow-hidden">
          <CardContent className="p-8 md:p-12 text-center relative">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyem0wLTRWMjhIMjR2MmgxMnptLTEyIDh2Mmg0di0yaDR2MmgtNHYyaC00di0ySDI0di0yaDR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
            <div className="relative">
              <img
                src="https://www.wadhwanifoundation.org/assets/svg/wf-logo-big.svg"
                alt="Wadhwani Foundation"
                className="h-10 mx-auto mb-6 brightness-0 invert"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit' }}>
                Start Your Upskilling Journey
              </h2>
              <p className="text-indigo-200 mb-8 max-w-lg mx-auto">
                Wadhwani Foundation offers free, industry-aligned programs to help educators and professionals build skills that matter.
              </p>
              <a href={link} target="_blank" rel="noopener noreferrer" onClick={() => trackClick('wadhwani_upskilling', link)}>
                <Button
                  size="lg"
                  className="bg-white text-indigo-700 hover:bg-indigo-50 font-semibold px-8 py-3 text-base"
                  data-testid="upskilling-cta-btn"
                >
                  Explore Courses <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-400 mt-8">
          Powered by EdAssist in partnership with Wadhwani Foundation
        </p>
      </div>
    </PublicLayout>
  );
};

export default TrainingProgramsPage;
